/**
 * Does every part on the sheet have a board part to become?
 *
 * A symbol names its pattern — `7404` → `DIP14` — and Net List In looks that
 * name up. When the lookup fails the package is simply skipped, and the only
 * account of it is a line in the transfer summary; a sheet that names nothing
 * at all (which is most of them: 1,694 of 4,773 packages across the sample
 * designs) loads onto a board and places nothing.
 *
 * So the check is worth making at the point the sheet is opened, while there is
 * still somebody to ask. This module works out *what* to ask about; resolving
 * the names against a library, and putting the question, belong to the caller.
 *
 * **Nothing here touches connectivity.** The only field written is
 * `footprint` on the symbol — the same string the sheet would have carried had
 * it named its pattern in the first place. Pads, pins, signals and ids are left
 * alone, on the sheet and on the board, so a match made here can never move a
 * net.
 */

import type { CadDocument, ComponentEntity } from '../model.ts';
import { defaultPcbLayers } from '../model.ts';
import { instantiate } from '../geom/transform.ts';
import { remapComponentLayers } from '../geom/board.ts';
import { packageOf } from './annotate.ts';
import { refDesOf } from './netlist.ts';
import { footprintAliases } from '../lib/catalogue.ts';

/** One physical package on the sheet, and what it says it becomes. */
export interface FootprintNeed {
  /** Package designator, e.g. `U1`. */
  ref: string;
  /** Part name, e.g. `7404`. */
  part: string;
  /** The pattern the sheet names, or null when it names none. */
  footprint: string | null;
  /** Ids of the gate symbols making up this package. */
  gateIds: number[];
  /** Gate designators, e.g. `U1a`, `U1b`. */
  gates: string[];
  /** Pins this package brings to the board — how much is at stake. */
  pins: number;
}

/** How a need was settled. */
export type FootprintStatus = 'named' | 'by-part-name' | 'unresolved';

/** A need, with the answer a library gave for it. */
export interface FootprintCheck extends FootprintNeed {
  status: FootprintStatus;
  /** The name that actually resolved, when one did. */
  resolved: string | null;
}

/**
 * Every package on the sheet that a transfer would try to place.
 *
 * `$$` blocks are left out: the ports, the title block and the board outline
 * are drawing, and have no board part by design. So are gates still carrying a
 * `?` — those want annotating, which is a different complaint and already has
 * its own report.
 */
export function footprintNeeds(source: CadDocument | readonly CadDocument[]): FootprintNeed[] {
  const sheets: readonly CadDocument[] = Array.isArray(source) ? source : [source as CadDocument];
  const out = new Map<string, FootprintNeed>();

  for (const doc of sheets) {
    for (const e of doc.entities) {
      if (e.kind !== 'component') continue;
      const c = e as ComponentEntity;
      if (c.name.startsWith('$$')) continue;
      const ref = refDesOf(c);
      if (ref.startsWith('$$') || ref.includes('?')) continue;

      const key = packageOf(ref);
      let need = out.get(key);
      if (!need) {
        need = {
          ref: key, part: c.name, footprint: c.footprint ?? null,
          gateIds: [], gates: [], pins: 0,
        };
        out.set(key, need);
      }
      // A later gate may know the pattern when the first did not. It may also
      // be on another sheet: a package's gates are spread wherever they were
      // wanted, and the pattern is the package's, not the gate's.
      if (!need.footprint && c.footprint) need.footprint = c.footprint;
      need.gateIds.push(c.id);
      if (!need.gates.includes(ref)) need.gates.push(ref);
      for (const child of c.children) {
        if (child.kind === 'pad' && child.pin) need.pins++;
      }
    }
  }

  return [...out.values()].sort(
    (a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
}

/**
 * Ask a library about each need.
 *
 * The fallback to the part name is the transfer's own and is kept identical
 * here, or the check would report as missing what the transfer then places
 * perfectly well. It is still reported as a guess: a CIRCAD symbol and its
 * pattern very often share a name, and very often is not always.
 */
export async function checkFootprints(
  source: CadDocument | readonly CadDocument[],
  find: (name: string) => Promise<ComponentEntity | null>,
): Promise<FootprintCheck[]> {
  const needs = footprintNeeds(source);
  const seen = new Map<string, boolean>();
  const known = async (name: string): Promise<boolean> => {
    const hit = seen.get(name);
    if (hit !== undefined) return hit;
    const found = (await find(name)) !== null;
    seen.set(name, found);
    return found;
  };

  const out: FootprintCheck[] = [];
  for (const need of needs) {
    if (need.footprint && await known(need.footprint)) {
      out.push({ ...need, status: 'named', resolved: need.footprint });
    } else if (need.part && await known(need.part)) {
      out.push({ ...need, status: 'by-part-name', resolved: need.part });
    } else {
      out.push({ ...need, status: 'unresolved', resolved: null });
    }
  }
  return out;
}

/**
 * Name a package's pattern, on every gate of it.
 *
 * Writes one field and nothing else. The sheet's wiring, the board's copper and
 * every id on both are untouched, so this is safe to run on a design that is
 * already netlisted and already routed: the next Net List In simply finds the
 * pattern it could not find before.
 *
 * Returns the number of symbols changed.
 */
export function setFootprint(
  source: CadDocument | readonly CadDocument[],
  ref: string,
  name: string,
): number {
  const sheets: readonly CadDocument[] = Array.isArray(source) ? source : [source as CadDocument];
  let n = 0;
  for (const doc of sheets) {
    for (const e of doc.entities) {
      if (e.kind !== 'component' || e.name.startsWith('$$')) continue;
      // Matched on the package designator rather than on the gate ids that
      // found it. A designator names one physical part across the whole design;
      // an entity id is only unique within its own sheet, and two sheets loaded
      // from separate files can hold the same id for different components.
      if (packageOf(refDesOf(e)) !== ref) continue;
      if (e.footprint === name) continue;
      e.footprint = name;
      n++;
    }
  }
  return n;
}

/**
 * Do two names name the same pattern?
 *
 * They very often do. The curated catalogue is a rename over the stock
 * libraries and both spellings are shipped — `DIP8` from the 1998 `.LIB` files
 * and `DIP-8` drawn from it — so a board placed under one and a sheet naming
 * the other are talking about one package, and comparing the strings would say
 * otherwise. That answer is not merely wrong; acted on, it rebuilds a routed
 * board from under its own copper for nothing.
 */
export function samePattern(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = a?.trim().toUpperCase() ?? '';
  const y = b?.trim().toUpperCase() ?? '';
  if (!x || !y) return false;
  if (x === y) return true;
  return footprintAliases(x).some((n) => n.toUpperCase() === y)
    || footprintAliases(y).some((n) => n.toUpperCase() === x);
}

/** The stack library footprints are authored against. */
const STANDARD_STACK = defaultPcbLayers();

/**
 * Put a different pattern under a part already on a board.
 *
 * Naming a footprint on a *symbol* is a label and cannot disturb anything —
 * that is `setFootprint` above. On a board it is not a label: the pads are the
 * part, so changing it means replacing the thing. There was no way to do that
 * at all, which is why a footprint chosen wrongly used to be permanent.
 *
 * Kept: where it was put, which way round, which side, and every BOM field.
 * Replaced: the pads and the outline. Not carried over: the pad signals, since
 * the new pads may not even be called the same things — Net List In writes
 * those, and running it after this is what finishes the job.
 *
 * Any track already run to the old pads stays exactly where it was drawn. It
 * has to be looked at: this is a change of package, and no rule about where
 * copper should go survives one.
 *
 * Returns the part now on the board, or null when it was not on this one.
 */
export function refitComponent(
  board: CadDocument,
  comp: ComponentEntity,
  pattern: ComponentEntity,
  named?: string,
): ComponentEntity | null {
  const at = board.entities.indexOf(comp);
  if (at < 0) return null;

  // Fresh ids throughout: sharing pad ids between two footprints fuses them in
  // the netlist, which keys nodes by id.
  const next = instantiate(pattern);
  // Footprints are authored against the standard stack; move them onto
  // whatever stack this board actually has, matching layers by role.
  remapComponentLayers(next, STANDARD_STACK, board.layers);

  next.x = comp.x;
  next.y = comp.y;
  next.rotation = comp.rotation;
  next.mirrored = comp.mirrored;
  next.fixed = comp.fixed;
  next.layer = comp.layer;
  next.params = { ...comp.params };
  next.footprint = named ?? pattern.name;

  board.entities[at] = next;
  return next;
}

/**
 * Resolve a pattern name, trying every name it answers to.
 *
 * The curated catalogue is a rename, and a rename has to work in both
 * directions or it is a break: a sheet saved today names `DIP-8` and one saved
 * in 1998 names `DIP8`, and the same geometry answers both. It also carries the
 * curated names anywhere the catalogue has not been ingested — the development
 * server parses the stock `.LIB` files directly and has never heard of
 * `PKG-THD`.
 *
 * **One implementation on purpose.** There were two: the Footprint Check used
 * a resolver that knew about aliases and the netlist transfer built its own
 * that did not, so a sheet whose patterns Footprint Check had just resolved
 * came back from Load onto Board as "no pattern could be found for any of the 8
 * parts". Every part had a pattern; the transfer was looking up the wrong
 * names. Anything that resolves a footprint goes through here.
 */
export async function resolvePattern(
  name: string,
  find: (name: string) => Promise<ComponentEntity | null> | ComponentEntity | null,
): Promise<ComponentEntity | null> {
  const direct = await find(name);
  if (direct) return direct;
  for (const alias of footprintAliases(name)) {
    const hit = await find(alias);
    if (hit) return hit;
  }
  return null;
}
